import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Icon } from 'js/design_system/Icon';
import CloseIcon from 'js/design_system/icons/close.svg';
import {
  DeprecatedGrid,
  GridColumn,
} from 'js/design_system/grid/DeprecatedGrid';
import getResponsivenessTraits, {
  PageName,
} from 'js/header/responsivenessTraits';
import SearchBar from 'js/header/SearchBar/SearchBar';
import { currentUser } from 'js/utils/user';
import { get } from 'js/utils/api_utils';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import AcademiaIcon from './AcademiaIcon';
import {
  Arm,
  SurfaceArgs,
  resolveSurfaces,
} from './autotest/AutotestFocusedNav';
import {
  ReactArm,
  ReactSurfaceArgs,
  resolveReactivationSurfaces,
} from './autotest/AutotestFocusedNavReactivation';
import MentionsCtaButton from './MentionsCtaButton';
import AccountMenu from './AccountMenu';
import AccountMenuButton from './AccountMenuButton';
import SmallAccountMenuButton from './SmallAccountMenuButton';
import ToolsMenu from './ToolsMenu';
import ToolsSidebar from './ToolsSidebar';
import { grantsMarketingIconButton } from './GrantsMarketingButton';
import ImpactNavButton from './ImpactNavButton';
import PodcastLibraryButton from './PodcastLibraryButton';
import PremiumBottomTab from './PremiumBottomTab';
import PremiumUpsellButton from './PremiumUpsellButton';
import SocialProofLine from './SocialProofLine';
import UploadButton from './UploadButton';
import {
  GreenDot,
  IconAnchor,
  IconButton,
  IconContainer,
  IconLabel,
  NavCountBubble,
  NotificationDot,
  RelativeIconContainer,
} from './buttonStyles';
import { setDocumentTitleCount } from './documentTitleCount';
import {
  CoreSlot,
  EXPERIMENTAL_NAV_ITEMS,
  ExperimentalNavItem,
} from './experimentalNavItems';
import { FocusedNavDynamicProps } from './types';

// The nav switches to the mobile style below this value (equal to $ds-tablet).
// If changing this value must also update '.focused-nav-container' styles.
export const HAMBURGER_MENU_SIZE = 768; // px
const NAV_HEIGHT_DESKTOP = 80;
export const NAV_HEIGHT_MOBILE = 56;

const NavAndTools = styled.div<{ isMobileNav: boolean }>`
  position: relative;
  z-index: 10;
  height: ${NAV_HEIGHT_DESKTOP}px;
  &.isMobileNav {
    height: ${NAV_HEIGHT_MOBILE}px;
  }
`;

const NavContainer = styled.div<{ inDevStyle?: boolean; useGrid?: boolean }>`
  max-height: ${NAV_HEIGHT_DESKTOP}px;
  max-width: 100vw;
  background-color: var(--background-white, #ffffff);

  &.inDevStyle {
    background-color: aqua;
  }

  padding: 16px;
  &.useGrid {
    padding: 16px 0;
  }
`;

const MobileNavContainer = styled(NavContainer)<{ useGrid?: boolean }>`
  padding: 7px 12px;
  &.useGrid {
    padding: 7px 0;
  }
`;

const MobileSearchContainer = styled.div<{ hasBadge?: boolean }>`
  flex-grow: 1;
  min-width: 0;
  margin-left: var(--spacing-sm-24, 24px);

  // The account button's notification bubble overflows ~10px to its right
  // (right: -10px), eating into the 24px gap (leaving only ~14px of visual
  // space). When the badge is showing, widen the margin to clear it.
  &.hasBadge {
    margin-left: 28px;
  }
`;

const MobileUpsellContainer = styled.div`
  flex-shrink: 1;
  min-width: 0;
  max-width: 40%;
  overflow: hidden;
`;

const WidthContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > * {
    margin: 0;
    white-space: nowrap;
    > * {
      white-space: initial;
    }
  }
  > * ~ * {
    margin: 0 0 0 16px;
  }

  @media (max-width: ${HAMBURGER_MENU_SIZE}px) {
    > * ~ * {
      margin: 0 0 0 8px;
    }
  }
`;

const SearchBarContainer = styled.div`
  flex: 1 1 auto;
`;

const IconAnchorsContainer = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: space-between;
  max-width: 380px;

  > * {
    margin: 0 8px;
  }
`;

const FullViewMenu = styled.div`
  position: fixed;
  top: calc(var(--nav-mobile-height, ${NAV_HEIGHT_MOBILE}px) + 2px);
  left: 0;
  width: 100vw;
  height: calc(100svh - var(--nav-mobile-height, ${NAV_HEIGHT_MOBILE}px) - 2px);
  overflow-y: scroll;
  overflow-x: hidden;
  background: white;
  padding: 16px;
  padding-bottom: 64px;
`;

const CloseMenuButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  margin: 0;
  padding: 12px 16px;
  border: none;
  background: transparent;
`;

const BottomMenu = styled.div`
  box-shadow: 0px -2px 4px 0px #00000014;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  background: white;
  flex: 1 1 auto;
  display: flex;
  justify-content: space-evenly;
  height: 48px;

  @media (max-width: 300px) {
    > *:nth-child(3) {
      display: none;
    }
  }
`;

const RestrictedUserBannerContainer = styled(Body)`
  width: 100%;
  padding: 12px;
  background-color: var(--brand-colors-peach);
  color: var(--text-primary);
  text-align: center;
`;

const RestrictedUserBannerLink = styled(BodyLink)`
  margin-left: 8px;
`;

type RestrictedUserBannerProps = {
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
};

const RestrictedUserBanner = ({
  restrictedUser,
  confirmAccountEmail,
}: RestrictedUserBannerProps) => {
  if (!restrictedUser) {
    return null;
  }

  switch (restrictedUser) {
    case 'unconfirmed':
      return (
        <RestrictedUserBannerContainer>
          Please confirm your account. We&apos;ve sent a confirmation link to{' '}
          {confirmAccountEmail}.
          <RestrictedUserBannerLink underline href="/confirm/send_new_email">
            Resend
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/confirm/sent#email">
            Change Email
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious by an automated system. If this
          was an error, please accept our apologies.
          <RestrictedUserBannerLink underline href="/user_unflag_requests/new">
            Request Unflagging
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged_pending':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious, and your reconsideration
          request is awaiting manual review. We&apos;re sorry for any
          inconvenience.
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    default: {
      const exhaustiveCheck: never = restrictedUser;
      throw new Error(`Unhandled restricted user type: ${exhaustiveCheck}`);
    }
  }
};

type ResponsiveIcon = {
  icon: ReactNode;
  // Lower priority wins on smaller screens.
  priority: number;
};

type ContainerProps = {
  useGrid: boolean;
  maxWidth: number | undefined;
  children: React.ReactNode;
};

const Container = ({ useGrid, maxWidth, children }: ContainerProps) =>
  useGrid ? (
    <DeprecatedGrid>
      <GridColumn sm={2} md={8} lg={12} xl={12}>
        <WidthContainer>{children}</WidthContainer>
      </GridColumn>
    </DeprecatedGrid>
  ) : (
    <WidthContainer style={{ maxWidth }}>{children}</WidthContainer>
  );

type Menu = 'account' | 'tools' | 'search';

export type RestrictedUserType = 'unconfirmed' | 'flagged' | 'flagged_pending';

/**
 * Public props for the logged-in nav. These are the same props the server
 * passes through `react_component('FocusedNav', ...)`; the `FocusedNav` wrapper
 * accepts exactly this shape and supplies `dynamicProps` to the view.
 */
export type FocusedNavProps = {
  inDevStyle?: boolean;
  pageName: PageName;
  useGrid?: boolean;
  initialQuery?: string;
  isPremium?: boolean;
  hidePremiumUpsellButton?: boolean;
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
  showCitationsDashboard?: boolean;
  /**
   * Activation autotest arm (nav_activation_2026_q2_v1), bucketed server-side in
   * FocusedNavPresenter and passed to AutotestFocusedNav.resolveSurfaces(...).
   * Defaults to 'control' (users not in the test, Storybook). While ARMS is empty
   * (A/A launch) every arm renders identically.
   */
  navArm?: Arm;
  /**
   * Reactivation autotest arm (nav_reactivation_2026_q2_v1), bucketed server-side
   * for formerly-premium (churned) users and passed to
   * AutotestFocusedNavReactivation.resolveReactivationSurfaces(...). Defaults to
   * 'control' (premium / cold / Storybook).
   */
  navReactivationArm?: ReactArm;
};

/**
 * Presentational logged-in nav. Owns all of the nav's rendering and UI state
 * (open menu, responsive icon trimming, scroll lock) but takes the async
 * `dynamicProps` payload — notifications, premium upsells, feature flags — as a
 * single injected object rather than fetching it. The live `FocusedNav` wrapper
 * supplies it from `useFocusedNavDynamicProps`; Storybook supplies a stub. This
 * is the seam that lets stories drive every state without fetch mocking.
 */
export type FocusedNavViewProps = FocusedNavProps & {
  dynamicProps?: FocusedNavDynamicProps;
  /**
   * Test seam: the experimental nav registry to render. Defaults to the live
   * `EXPERIMENTAL_NAV_ITEMS`; Storybook/tests can inject a synthetic list (e.g. to
   * exercise the replace path) without shipping it.
   */
  experimentalNavItems?: ExperimentalNavItem[];
  /**
   * Test seam: seeds the initial Mentions unread count. In production the count is
   * fetched from a live endpoint; Storybook/tests use this to demonstrate the badge.
   */
  initialMentionsCount?: number;
};

const FocusedNavView = ({
  inDevStyle,
  pageName,
  useGrid = false,
  initialQuery,
  isPremium = false,
  hidePremiumUpsellButton,
  restrictedUser,
  confirmAccountEmail,
  showCitationsDashboard,
  navArm = 'control',
  navReactivationArm = 'control',
  dynamicProps,
  experimentalNavItems = EXPERIMENTAL_NAV_ITEMS,
  initialMentionsCount = 0,
}: FocusedNavViewProps) => {
  const iconSize = 24;

  // Allow conditional rendering of components that aren't SSR-safe
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const notifications = dynamicProps?.notifications;
  const premiumUpsells = dynamicProps?.premium_upsells;
  const featureFlags = dynamicProps?.feature_flags;

  // Feature flags loaded asynchronously
  const showGrantsButtonOnNav = featureFlags?.showGrantsButtonOnNav ?? false;
  const showImpactHubNav = featureFlags?.showImpactHubNav ?? false;
  const showPodcastLibraryNav = featureFlags?.showPodcastLibraryNav ?? false;
  const showCitationsDashboardNav = showCitationsDashboard ?? false;

  // Experimental nav items (declared in ./experimentalNavItems) whose feature flag
  // is enabled for this user. Each may take over a core slot (`replaces`) or be
  // purely additive. See renderExperimentalItem + the desktop/mobile assembly below.
  const enabledExperimentalItems = useMemo(
    () =>
      experimentalNavItems.filter(
        (item) =>
          (featureFlags?.[item.flag] ?? false) &&
          (!item.premiumOnly || isPremium),
      ),
    [experimentalNavItems, featureFlags, isPremium],
  );

  const [openMenu, setOpenMenu] = useState<null | Menu>(null);
  const { screenWidth } = useScreenSize();
  const { maxWidth } = getResponsivenessTraits(pageName, screenWidth);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [mentionsCount, setMentionsCount] = useState(initialMentionsCount);

  const isMobileNav = screenWidth < HAMBURGER_MENU_SIZE;

  // Plugs-and-sockets resolver (Step 8 AT-prep). The arm (`navArm`) is bucketed
  // server-side in FocusedNavPresenter and passed as a prop; resolveSurfaces
  // merges the arm's overrides over CONTROL. The test launches as an A/A (ARMS
  // empty), so `surfaces` always equals CONTROL and rendering is unchanged. Step 9
  // populates ARMS with variants. See ./autotest/AutotestFocusedNav and
  // autotest-work/navbar/socket-plan.md.
  const surfaceArgs: SurfaceArgs = useMemo(
    () => ({
      isPremium,
      isMobileNav,
      pageName,
      trialPricingCopy: premiumUpsells?.trialPricingCopy ?? null,
      mentionsCount,
      // B1 (v_cta_duration): the REAL trial price + humanized period from the
      // offer. Both null when no trial is available → the arm falls back to the
      // offer-derived control copy rather than inventing terms.
      trialPriceLabel: premiumUpsells?.trialPrice ?? null,
      trialPeriodLabel: premiumUpsells?.humanizedTrialPeriod ?? null,
      liveReaderCount: null,
    }),
    [
      isPremium,
      isMobileNav,
      pageName,
      premiumUpsells?.trialPricingCopy,
      mentionsCount,
      premiumUpsells?.trialPrice,
      premiumUpsells?.humanizedTrialPeriod,
    ],
  );
  const surfaces = useMemo(
    () => resolveSurfaces(navArm, surfaceArgs),
    [navArm, surfaceArgs],
  );

  // Reactivation autotest (nav_reactivation_2026_q2_v1): overrides copy on the
  // churned "Reactivate Premium" CTA. Only churned users are bucketed (others get
  // 'control' → reactivateLabel null → today's offer-derived text). Uses the REAL
  // offer type/value so the discount-emphasis arm never fabricates a number.
  const reactSurfaceArgs: ReactSurfaceArgs = useMemo(
    () => ({
      isMobileNav,
      reactivateOffer:
        premiumUpsells?.churnedUserReactivationDiscount?.offer ?? null,
      reactivateValue:
        premiumUpsells?.churnedUserReactivationDiscount?.value ?? null,
      reactivateMentions:
        premiumUpsells?.churnedUserReactivationDiscount?.mentions ?? null,
    }),
    [
      isMobileNav,
      premiumUpsells?.churnedUserReactivationDiscount?.offer,
      premiumUpsells?.churnedUserReactivationDiscount?.value,
      premiumUpsells?.churnedUserReactivationDiscount?.mentions,
    ],
  );
  const reactSurfaces = useMemo(
    () => resolveReactivationSurfaces(navReactivationArm, reactSurfaceArgs),
    [navReactivationArm, reactSurfaceArgs],
  );

  // E1 autotest (arm v_mobile_premium_tab): move the mobile premium upsell from
  // the top-right icon into a dedicated BottomMenu tab. Only active for the
  // variant surface AND when the top upsell icon would otherwise show (i.e.
  // non-premium mobile, see `mobileTopIcon === 'upsell_top'` below). CONTROL keeps
  // `mobilePremiumPlacement: 'top_icon'`, so this is always false → unchanged.
  const usePremiumBottomTab = surfaces.mobilePremiumPlacement === 'bottom_tab';

  // Which icon to show at top of mobile nav
  const mobileTopIcon = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (isPremium) {
      return 'tools_top';
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library_top';
    }
    return 'upsell_top';
  }, [isPremium, restrictedUser, showPodcastLibraryNav]);

  // A button to show next to the menu on desktop
  const desktopHighlightedButton = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library';
    }
    if (!isPremium && !hidePremiumUpsellButton) {
      return 'premium_upsell';
    }
    return null;
  }, [
    hidePremiumUpsellButton,
    isPremium,
    restrictedUser,
    showPodcastLibraryNav,
  ]);

  useEffect(() => {
    const url = routes.unread_count_api_v0_notification_messages_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setNotificationCount(count));
  }, []);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setMentionsCount(count));
  }, []);

  // Unread counts for experimental items that declare an `unreadCount` source,
  // keyed by item.key. Generalizes the old per-feature radar effect.
  const [experimentalUnreadCounts, setExperimentalUnreadCounts] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    enabledExperimentalItems.forEach((item) => {
      if (!item.unreadCount) return;
      const { route, countField } = item.unreadCount;
      const url = route({ subdomain_param: 'api' });
      get(url).then((res) =>
        setExperimentalUnreadCounts((prev) => ({
          ...prev,
          [item.key]: res[countField] ?? 0,
        })),
      );
    });
  }, [enabledExperimentalItems]);

  const experimentalUnreadTotal = Object.values(
    experimentalUnreadCounts,
  ).reduce((sum, count) => sum + count, 0);

  // Citations-dashboard users get a dot (not a number) for new mentions, so the
  // mentions count is excluded from the account badge total.
  const visibleMentionsCount = showCitationsDashboardNav ? 0 : mentionsCount;
  const totalCount =
    notificationCount +
    visibleMentionsCount +
    experimentalUnreadTotal +
    (notifications?.messagesCount ?? 0) +
    (notifications?.hasUnseenReaders ? 1 : 0);

  // SSR-safe: Update document title in useEffect to avoid hydration mismatch
  useEffect(() => {
    setDocumentTitleCount(totalCount);
  }, [totalCount]);

  const toggleMenu = useCallback(
    (menu: Menu): void => {
      if (openMenu === menu) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menu);
      }
    },
    [openMenu],
  );

  const homeButton = useMemo(
    () => (
      <IconAnchor
        key="home"
        href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
      >
        <IconContainer>
          <Icon name="home" size={iconSize} />
        </IconContainer>
        <IconLabel>Home</IconLabel>
      </IconAnchor>
    ),
    [iconSize],
  );

  const impactButton = useMemo(() => <ImpactNavButton />, []);

  const mentionsButton = useMemo(() => {
    if (showCitationsDashboardNav) {
      return (
        <ImpressionWatcher key="citations" source="citations.nav">
          <IconAnchor
            href={routes.citations_url({ from_navbar: true, trigger: 'nav' })}
          >
            <IconContainer>
              <Icon name="format_quote" size={iconSize} />
            </IconContainer>
            <IconLabel>
              Citations
              {mentionsCount > 0 &&
                (surfaces.mentionsBadge === 'count' ? (
                  <NavCountBubble count={mentionsCount} />
                ) : (
                  <NotificationDot />
                ))}
            </IconLabel>
          </IconAnchor>
        </ImpressionWatcher>
      );
    }

    // Autotest `v_mentions_cta` (C3): a non-premium user with unseen mentions
    // sees a direct premium CTA instead of the icon→page item. Control leaves
    // `mentionsAsCta` false → the existing mentions item renders unchanged.
    if (surfaces.mentionsAsCta && !isPremium && mentionsCount > 0) {
      return <MentionsCtaButton iconSize={iconSize} />;
    }

    return (
      <ImpressionWatcher key="mentions" source="mentions.nav">
        <IconAnchor
          href={routes.mentions_url({ from_navbar: true, trigger: 'nav' })}
        >
          <IconContainer>
            <Icon name="format_quote" size={iconSize} />
          </IconContainer>
          <IconLabel>
            {surfaces.mentionsLabel}
            {mentionsCount > 0 &&
              (surfaces.mentionsBadge === 'count' ? (
                <NavCountBubble count={mentionsCount} />
              ) : (
                <NotificationDot />
              ))}
          </IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    );
  }, [
    mentionsCount,
    showCitationsDashboardNav,
    iconSize,
    isPremium,
    surfaces.mentionsAsCta,
    surfaces.mentionsLabel,
    surfaces.mentionsBadge,
  ]);

  const analyticsButton = useMemo(
    () =>
      isClient ? (
        <IconAnchor
          key="analytics"
          href={routes.analytics_dashboard_url({
            page_name: currentUser?.page_name,
            subdomain: currentUser?.domain_name,
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <Icon name="bar_chart" size={iconSize} />
          </IconContainer>
          <IconLabel>Analytics</IconLabel>
        </IconAnchor>
      ) : null,
    [isClient, iconSize],
  );

  const uploadButton = useMemo(
    () => (
      <UploadButton
        key="upload"
        hasUnseenClaimablePapers={
          notifications?.hasUnseenClaimablePapers ?? false
        }
        hasUnseenUploadableStubs={
          notifications?.hasUnseenUploadableStubs ?? false
        }
      />
    ),
    [
      notifications?.hasUnseenClaimablePapers,
      notifications?.hasUnseenUploadableStubs,
    ],
  );

  const toolsButton = useMemo(
    () => (
      <IconButton
        ref={toolsButtonRef}
        key="tools"
        onClick={() => toggleMenu('tools')}
        aria-pressed={openMenu === 'tools'}
      >
        <IconContainer>
          <Icon name="apps" size={iconSize} />
        </IconContainer>
        <IconLabel>Tools</IconLabel>
      </IconButton>
    ),
    [openMenu, toggleMenu, iconSize],
  );

  const grantsMultiplierButton = useMemo(() => grantsMarketingIconButton, []);

  // Renders a registry-declared experimental item as a standard nav icon. `source`
  // distinguishes the desktop vs mobile instance (for analytics + React keys).
  const renderExperimentalItem = useCallback(
    (item: ExperimentalNavItem, source: 'desktop' | 'mobile'): ReactNode => {
      const unread = experimentalUnreadCounts[item.key] ?? 0;
      const showUnreadDot = Boolean(item.unreadCount) && unread > 0;
      return (
        <ImpressionWatcher
          key={`${item.key}-${source}`}
          source={item.impressionSource ?? `${item.key}.nav`}
        >
          <IconAnchor
            href={item.route({ from_navbar: true, trigger: 'nav' })}
            onClick={() => item.onNavClick?.(source)}
          >
            <RelativeIconContainer>
              <Icon name={item.iconName} size={24} />
              {(item.showNewDot ?? true) && <GreenDot />}
              {showUnreadDot &&
                (item.unreadCount?.dotColor === 'green' ? (
                  <GreenDot />
                ) : (
                  <NotificationDot />
                ))}
            </RelativeIconContainer>
            <IconLabel>{item.label}</IconLabel>
          </IconAnchor>
        </ImpressionWatcher>
      );
    },
    [experimentalUnreadCounts],
  );

  const icons = useMemo(() => {
    const showImpactHub = showImpactHubNav && !showGrantsButtonOnNav;
    // Grants icon for everyone; experimental items via the registry.
    const showGrantMultiplier = !isMobileNav && showGrantsButtonOnNav;

    // Core slots an experimental item may take over on desktop.
    const slotNodes: Record<CoreSlot, ReactNode> = {
      home: homeButton,
      mentions: mentionsButton,
      analytics: analyticsButton,
      upload: uploadButton,
    };
    const additiveItems: ExperimentalNavItem[] = [];
    enabledExperimentalItems.forEach((item) => {
      if (item.replaces) {
        slotNodes[item.replaces] = renderExperimentalItem(item, 'desktop');
      } else {
        additiveItems.push(item);
      }
    });

    const unfilteredIcons: (ResponsiveIcon | false)[] = [
      { icon: slotNodes.home, priority: 5 },
      showImpactHub && { icon: impactButton, priority: 2 },
      { icon: slotNodes.mentions, priority: 0 },
      showGrantMultiplier && { icon: grantsMultiplierButton, priority: 4 },
      { icon: slotNodes.analytics, priority: 1 },
      // Additive experimental items render after the core nav icons, before the
      // Upload/Tools action cluster.
      ...additiveItems.map((item) => ({
        icon: renderExperimentalItem(item, 'desktop'),
        priority: item.priority,
      })),
      { icon: slotNodes.upload, priority: 3 },
      { icon: toolsButton, priority: -Infinity },
    ];
    const icons: ResponsiveIcon[] = unfilteredIcons.filter(
      // Type guard is needed here to convince typescript that there are no false values
      (icon): icon is ResponsiveIcon => !!icon,
    );

    if (isPremium || hidePremiumUpsellButton) {
      return icons.map((icon) => icon.icon);
    }

    // Hide another icon every 50 pixels past the max width.
    const iconsWithPriority = icons.filter(
      (icon) => icon.priority !== undefined,
    ).length;
    const baseMaxWidth = 950; // Arbitrary pixels that respond best
    const maxWidth =
      baseMaxWidth + (premiumUpsells?.reactivatePremiumUpsellType ? 273 : 127); // Premium button sizes
    const maxLevel =
      iconsWithPriority -
      Math.floor((maxWidth - Math.min(screenWidth, maxWidth)) / 50);
    return icons
      .filter((icon) => icon.priority <= maxLevel)
      .map((icon) => icon.icon);
  }, [
    premiumUpsells?.reactivatePremiumUpsellType,
    screenWidth,
    analyticsButton,
    homeButton,
    impactButton,
    mentionsButton,
    uploadButton,
    toolsButton,
    hidePremiumUpsellButton,
    isMobileNav,
    grantsMultiplierButton,
    showGrantsButtonOnNav,
    showImpactHubNav,
    isPremium,
    enabledExperimentalItems,
    renderExperimentalItem,
  ]);

  // Mobile bottom-bar experimental placement: replace a core item in the bar
  // (home/mentions/analytics) when targeted, otherwise append as a trailing tab.
  const mobileExperimental = useMemo(() => {
    const MOBILE_BAR_SLOTS: CoreSlot[] = ['home', 'mentions', 'analytics'];
    const slotOverrides: Partial<Record<CoreSlot, ReactNode>> = {};
    const appended: ReactNode[] = [];
    enabledExperimentalItems
      .filter((item) => item.showInMobile !== false)
      .forEach((item) => {
        if (item.replaces && MOBILE_BAR_SLOTS.includes(item.replaces)) {
          slotOverrides[item.replaces] = renderExperimentalItem(item, 'mobile');
        } else {
          appended.push(renderExperimentalItem(item, 'mobile'));
        }
      });
    return { slotOverrides, appended };
  }, [enabledExperimentalItems, renderExperimentalItem]);

  useScrollLock(isMobileNav && openMenu !== null);

  const closeMenuButton = (
    <CloseMenuButton onClick={() => setOpenMenu(null)}>
      <CloseIcon color="#222222" />
    </CloseMenuButton>
  );

  if (isMobileNav) {
    return (
      <>
        <NavAndTools isMobileNav={isMobileNav}>
          <MobileNavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
            <Container maxWidth={maxWidth} useGrid={useGrid}>
              <SmallAccountMenuButton
                count={totalCount}
                menuOpen={openMenu === 'account'}
                setMenuOpen={(open) => setOpenMenu(open ? 'account' : null)}
                isPremium={isPremium}
                accountAvatarUpsell={surfaces.accountAvatarUpsell}
              />
              <MobileSearchContainer hasBadge={totalCount > 0}>
                <ImpressionWatcher source="focused_nav_search_bar">
                  <SearchBar
                    initialQuery={initialQuery}
                    isPremium={isPremium}
                  />
                </ImpressionWatcher>
              </MobileSearchContainer>
              {mobileTopIcon === 'tools_top' && (
                <div onClick={() => toggleMenu('tools')}>{toolsButton}</div>
              )}
              {mobileTopIcon === 'upsell_top' && !usePremiumBottomTab && (
                <MobileUpsellContainer>
                  <PremiumUpsellButton
                    premiumUpsells={premiumUpsells}
                    isMobileNav={isMobileNav}
                    ctaLabel={surfaces.ctaLabel}
                    ctaIcon={surfaces.ctaIcon}
                    ctaEmphasis={surfaces.ctaEmphasis}
                    reactivateLabel={reactSurfaces.reactivateLabel}
                    reactivateDiscountBadge={
                      reactSurfaces.reactivateDiscountBadge
                    }
                  />
                </MobileUpsellContainer>
              )}
              {mobileTopIcon === 'podcast_library_top' && (
                <PodcastLibraryButton />
              )}
            </Container>
          </MobileNavContainer>
          {openMenu === 'tools' && (
            <FullViewMenu>
              {closeMenuButton}
              <ToolsMenu
                hasUnseenClaimablePapers={
                  notifications?.hasUnseenClaimablePapers ?? false
                }
                hasUnseenUploadableStubs={
                  notifications?.hasUnseenUploadableStubs ?? false
                }
                isPremium={isPremium}
              />
            </FullViewMenu>
          )}
          {openMenu === 'account' && (
            <FullViewMenu>
              {closeMenuButton}
              <AccountMenu
                restrictedUser={Boolean(restrictedUser)}
                halfOffCoupon={premiumUpsells?.halfOffCoupon}
                reactivate={premiumUpsells?.reactivatePremiumUpsellType}
                mentionsCount={mentionsCount}
                messagesCount={notifications?.messagesCount ?? 0}
                hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
                notificationCount={notificationCount}
                showCitationsDashboard={showCitationsDashboardNav}
                isPremium={isPremium}
                accountUpgradeStyle={surfaces.accountUpgradeStyle}
              />
            </FullViewMenu>
          )}
          {!restrictedUser && (
            <BottomMenu>
              {mobileExperimental.slotOverrides.home ?? homeButton}
              {showImpactHubNav && !showGrantsButtonOnNav && impactButton}
              {mobileExperimental.slotOverrides.mentions ?? mentionsButton}
              {mobileExperimental.slotOverrides.analytics ?? analyticsButton}
              {mobileTopIcon !== 'tools_top' ? toolsButton : uploadButton}
              {mobileExperimental.appended}
              {usePremiumBottomTab && mobileTopIcon === 'upsell_top' && (
                <PremiumBottomTab />
              )}
            </BottomMenu>
          )}
        </NavAndTools>
        <RestrictedUserBanner
          restrictedUser={restrictedUser}
          confirmAccountEmail={confirmAccountEmail}
        />
      </>
    );
  }

  return (
    <>
      <NavAndTools isMobileNav={isMobileNav}>
        <NavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
          <Container maxWidth={maxWidth} useGrid={useGrid}>
            <AcademiaIcon />
            <SearchBarContainer>
              <ImpressionWatcher source="focused_nav_search_bar">
                <SearchBar initialQuery={initialQuery} isPremium={isPremium} />
              </ImpressionWatcher>
            </SearchBarContainer>
            {!restrictedUser && (
              <IconAnchorsContainer>{icons}</IconAnchorsContainer>
            )}
            {desktopHighlightedButton === 'premium_upsell' &&
              surfaces.socialProofSlot && (
                <SocialProofLine text={surfaces.socialProofSlot} />
              )}
            {desktopHighlightedButton === 'premium_upsell' && (
              <PremiumUpsellButton
                premiumUpsells={premiumUpsells}
                isMobileNav={isMobileNav}
                ctaLabel={surfaces.ctaLabel}
                ctaIcon={surfaces.ctaIcon}
                ctaEmphasis={surfaces.ctaEmphasis}
                reactivateLabel={reactSurfaces.reactivateLabel}
                reactivateDiscountBadge={reactSurfaces.reactivateDiscountBadge}
              />
            )}
            {desktopHighlightedButton === 'podcast_library' && (
              <PodcastLibraryButton />
            )}
            <AccountMenuButton
              restrictedUser={Boolean(restrictedUser)}
              halfOffCoupon={premiumUpsells?.halfOffCoupon}
              reactivate={premiumUpsells?.reactivatePremiumUpsellType}
              totalCount={totalCount}
              notificationCount={notificationCount}
              mentionsCount={mentionsCount}
              messagesCount={notifications?.messagesCount ?? 0}
              hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
              showCitationsDashboard={showCitationsDashboardNav}
              isPremium={isPremium}
              accountUpgradeStyle={surfaces.accountUpgradeStyle}
              accountAvatarUpsell={surfaces.accountAvatarUpsell}
              open={openMenu === 'account'}
              setOpen={(open) => setOpenMenu(open ? 'account' : null)}
            />
          </Container>
        </NavContainer>

        {openMenu === 'tools' && (
          <ToolsSidebar
            buttonRef={toolsButtonRef}
            close={() => setOpenMenu(null)}
            hasUnseenClaimablePapers={
              notifications?.hasUnseenClaimablePapers ?? false
            }
            hasUnseenUploadableStubs={
              notifications?.hasUnseenUploadableStubs ?? false
            }
            isPremium={isPremium}
          />
        )}
      </NavAndTools>
      <RestrictedUserBanner
        restrictedUser={restrictedUser}
        confirmAccountEmail={confirmAccountEmail}
      />
    </>
  );
};

export default FocusedNavView;
